import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useSelector, useDispatch, shallowEqual } from "react-redux";
import dayjs from "dayjs";
import Loader from "@features/shared/components/Loader";
import { FiArrowRight } from "react-icons/fi";
import styles from "@components/MarketVisitReport/MarketVisitReport.module.css";

// Import section components
import DateSection from "./DateSection";
import { getMinWorkingDate } from "./dateWindow";
import WSSSection from "./WSSSection";
import DealerSection from "./DealerSection";
import UserSection from "./UserSection";
import UsersSectionRegular from "./UsersSectionRegular";
import ProductSection from "./ProductSection";
import CompetitionSection from "./CompetitionSection";
import EscalationSection from "./EscalationSection";
import MiscellaneousSection from "./MiscellaneousSection";
import { SubmitModal, DraftModal, ErrorModal } from "./Modals";

// Import hooks
import {
  isEmpty,
  debounce,
  separateDates,
  CHARACTER_LIMITS,
  MAX_ESCALATION_LIMIT,
} from "../hooks/constants";

// Import services
import { marketVisitReportService } from "../services/marketVisitReportService";
import { addMarketVisitReportApi } from "@services/MarketVisitReport/addMarketVisitReportApi";
import { deleteSaveAsDraftForMVRApi } from "@services/MarketVisitReport/deleteSaveAsDraftForMVRAPI";
import { addEscalationServiceRequestApi } from "@services/MarketVisitReport/addEscalationServiceRequestApi";
import { updateMarketVisitReportApi } from "@services/MarketVisitReport/updateMarketVisitReportApi";
import { addDealerNameApi } from "@services/MarketWorking/MyTeam/addDealerNameApi";
import { getZSMWssNameApi } from "@services/zsm/getZSMWssNameApi";
import { addWSSNameApiForHOUser } from "@services/hoUser/addWSSNameApiForHOUser";
import { getMVRDealerNameApi } from "@services/MarketVisitReport/getDealerNameApi";
import { getMVRDealerNameForHOApi } from "@services/MarketVisitReport/getMVRDealerNameForHOApi";

// Other imports
import { clusterCodePlaceHolder } from "@components/Common/Constant";
import {
  getMVRDraftDetails,
  removeMVRDraftDetails,
} from "@redux/actions/mvr-action";
import "jspdf-autotable";

/**
 * MarketVisitReportForm Component
 * Main form component using new feature-based architecture
 */
const MarketVisitReportForm = () => {
  const dispatch = useDispatch();

  // State management
  const [cluster, setCluster] = useState(clusterCodePlaceHolder);
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formValidation, setFormValidation] = useState({});
  const [formData, setFormData] = useState({});
  const [productList, setProductList] = useState([]);
  const [wssList, setWssList] = useState([]);
  const [dealerList, setDealerList] = useState([]);
  const [wssName, setWssName] = useState([]);
  const [dealerName, setDealerName] = useState([]);
  const [userName, setUserName] = useState([]);
  const [disable, setDisable] = useState(false);
  const [characterLimitValidation, setCharacterLimitValidation] = useState({});
  const [isSubmitedSuccessfully, setIsSubmitedSuccessfully] = useState(false);
  const [selectUserType, setSelectUserType] = useState([]);
  const [vlcListType, setVlcListType] = useState([]);
  const [vlcList, setVlcList] = useState([]);

  const [submittedHighlightedDates, setSubmittedHighlightedDates] = useState([]);
  const [draftHighlightedDates, setDraftHighlightedDates] = useState([]);
  const [userType, setUserType] = useState([]);

  const [isDraftSave, setIsDraftSave] = useState(false);
  const [formDraftId, setFormDraftId] = useState(null);
  const [isButtonDisable, setIsButtonDisable] = useState(false);
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [draftedForm, setDraftedForm] = useState([]);
  const draftedFormRef = useRef([]);
  const [, setCurrentSelectedDate] = useState("");
  const [fileValidation] = useState({});

  // Escalation rows state
  const [rows, setRows] = useState([
    {
      srId: "101",
      assigned_to: "",
      issue: "",
      files: [],
      validationError: { file: "", issue: "" },
    },
  ]);

  // Redux selectors
  const salesGroupCode = useSelector((state) =>
    state?.userDetails?.details?.Sales_Group_Code__c !== null
      ? state?.userDetails?.details?.Sales_Group_Code__c
      : state?.SelectedSalesGroupCode?.SalesGroupCode,
    shallowEqual
  );

  const selectedInsights = useSelector(
    (state) => state?.insights?.selectedInsights,
    shallowEqual
  );

  const HoUserType = useSelector(
    (state) =>
      state?.userType?.type !== "Other"
        ? state?.userType?.type
        : state?.userDetails?.details?.Title,
    shallowEqual
  );

  const userId = useSelector(
    (state) => state?.userDetails?.details?.Id,
    shallowEqual
  );

  const clusterCodeOption = useSelector(
    (state) => state?.clusterCode?.clusterCodeList,
    shallowEqual
  );

  const shCode = useSelector(
    (state) => state?.userDetails?.details?.SH_Code__c,
    shallowEqual
  );

  const products = useSelector(
    (state) => state?.product?.productList,
    shallowEqual
  );

  const wssNameList = useSelector(
    (state) => state?.visitDetails?.checkInWSSDetails,
    shallowEqual
  );

  const dealerNameList = useSelector(
    (state) => state?.visitDetails?.checkInDetails,
    shallowEqual
  );

  const userNameList = useSelector(
    (state) => state?.myUserFeedback?.userList,
    shallowEqual
  );

  let drafts = useSelector((state) => state?.mvrDraft?.draft, shallowEqual);

  const userEmail = useSelector(
    (state) => state?.userDetails?.details?.Email,
    shallowEqual
  );

  const userDetailName = useSelector(
    (state) => state?.userDetails?.details?.Name,
    shallowEqual
  );

  const salesGroupUser = useSelector(
    (state) => state?.salesgroupUser?.salesgroupUserList,
    shallowEqual
  );

  const rbdmCode = useSelector(
    (state) => state?.RBDMCode?.RBDMCode[0]?.SH3_Code__c,
    shallowEqual
  );

  const tourPlanDefaultData = useSelector(
    (state) => state?.upcomingTourPlan?.upcomingTourDetails?.records,
    shallowEqual
  );

  const territoryOptions = useSelector(
    (state) => state?.territory?.territoryDetails,
    shallowEqual
  );

  const hoDivisionGroup = territoryOptions?.[0]?.Division_Code__c;
  const hoSalesGroup = territoryOptions?.[0]?.Sales_Group__c;

  // Sync filtered check-in names when date changes (matches production logic)
  useEffect(() => {
    if (!formData?.marketVisitDate) return;
    const newWss = [];
    const newDealer = [];
    const newUser = [];
    wssNameList.forEach((item) => {
      if (
        new Date(item?.Check_In_Date_Time__c?.split(",")?.[0]).toLocaleDateString("en-CA", {
          year: "numeric", month: "numeric", day: "numeric",
        }) === formData.marketVisitDate
      ) newWss.push(item?.WSS_Name__c);
    });
    dealerNameList.forEach((item) => {
      if (
        new Date(item?.Check_In_Date_Time__c?.split(",")?.[0]).toLocaleDateString("en-CA", {
          year: "numeric", month: "numeric", day: "numeric",
        }) === formData.marketVisitDate
      ) newDealer.push(item?.CheckIn_Dealer__c);
    });
    userNameList?.forEach((item) => {
      if (
        new Date(item?.CreatedDate?.split("T")?.[0]).toLocaleDateString("en-CA", {
          year: "numeric", month: "numeric", day: "numeric",
        }) === formData.marketVisitDate
      ) newUser.push(item?.User_Name_es__c);
    });
    setWssName(newWss);
    setDealerName(newDealer);
    setUserName(newUser);
  }, [formData?.marketVisitDate]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load product list for mentions
  useEffect(() => {
    const mapped = products.map((item) => ({
      id: item?.Id,
      display: item?.Product_Group_3_Description__c,
    }));
    setProductList(mapped);
  }, [products]);

  // Load WSS and Dealer name lists for BM (SH3) users — for @w / @d intelligent tagging
  useEffect(() => {
    if (userType === "SH3") {
      (async () => {
        const response = await addDealerNameApi("WSS");
        response?.data?.Name?.forEach((item) =>
          setWssList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.CustomerName__c}:${item?.Customer_Code__c}` },
          ])
        );
      })();
    }
    if (userType === "SH3" && shCode) {
      (async () => {
        const res = await dispatch(getMVRDealerNameApi(shCode));
        res?.data?.records?.forEach((item) =>
          setDealerList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.Dealer_Name__c}:${item?.DealerCode__c}` },
          ])
        );
      })();
    }
  }, [userType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load WSS and Dealer lists for ZSM/NSM/HO users when clusterCode changes
  useEffect(() => {
    if (!formData?.clusterCode) return;
    if (userType === "SH6" || userType === "SH5" || userType === "SH7") {
      setWssList([]);
      setDealerList([]);
      (async () => {
        const response = await dispatch(getZSMWssNameApi(formData.clusterCode));
        response?.data?.forEach((item) =>
          setWssList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.CustomerName__c}:${item?.Customer_Code__c}` },
          ])
        );
        const res = await dispatch(getMVRDealerNameApi(formData.clusterCode));
        res?.data?.records?.forEach((item) =>
          setDealerList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.Dealer_Name__c}:${item?.DealerCode__c}` },
          ])
        );
      })();
    }
    if (
      userType === "HO Collaborator" ||
      userType === "TMM" ||
      userType === "HO Auditor"
    ) {
      setWssList([]);
      setDealerList([]);
      (async () => {
        const response = await addWSSNameApiForHOUser(hoDivisionGroup, hoSalesGroup, formData.clusterCode);
        response?.data?.forEach((item) =>
          setWssList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.CustomerName__c}:${item?.Customer_Code__c}` },
          ])
        );
        const res = await dispatch(getMVRDealerNameForHOApi(hoDivisionGroup, hoSalesGroup, formData.clusterCode));
        res?.data?.records?.forEach((item) =>
          setDealerList((ls) => [
            ...ls,
            { id: item?.Id, display: `${item?.Dealer_Name__c}:${item?.DealerCode__c}` },
          ])
        );
      })();
    }
  }, [userType, formData?.clusterCode]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep ref in sync with draftedForm state so closures always see latest value
  useEffect(() => {
    draftedFormRef.current = draftedForm;
  }, [draftedForm]);

  // Initialize draftedForm from Redux on mount (mirrors production behaviour)
  useEffect(() => {
    if (userId) {
      const userDrafts = drafts?.[userId];
      if (Array.isArray(userDrafts) && userDrafts.length > 0) {
        // Update ref synchronously so checkFormData (called below) sees it immediately
        draftedFormRef.current = userDrafts;
        setDraftedForm(userDrafts);
      }
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Set user type
  useEffect(() => {
    setUserType(HoUserType);
  }, [HoUserType]);

  // Auto save functionality — uses ref so no stale closure on draftedForm
  const sendRequest = useCallback(
    (value) => {
      const current = draftedFormRef.current;
      const index = current.findIndex(
        (obj) => obj.marketVisitDate === value.marketVisitDate
      );
      if (index !== -1) {
        setDraftedForm((prevDataArray) => {
          const updatedArray = [...prevDataArray];
          updatedArray[index] = value;
          return updatedArray;
        });
      } else {
        setDraftedForm((prevDataArray) => [...prevDataArray, value]);
      }
    },
    [] // no deps — reads from ref, not closure
  );

  const debouncedApiCall = useMemo(() => {
    return debounce(sendRequest, 3000);
  }, [sendRequest]);

  useEffect(() => {
    (async () => {
      const updatedDrafts = { ...drafts, [userId]: draftedForm };
      await dispatch(getMVRDraftDetails(updatedDrafts));
    })();
  }, [draftedForm]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle form field changes
  const onChange = useCallback(
    async (e) => {
      const { value, name } = e.target;
      
      if (name === "marketVisitDate") {
        setDealerName([]);
        setWssName([]);
        setUserName([]);
      }
      
      if (name === "clusterCode") {
        setWssList([]);
        setDealerList([]);
      }

      setFormData({ ...formData, [name]: value });
      
      if (name !== "marketVisitDate") {
        debouncedApiCall({ ...formData, rows, [name]: value });
      }

      // Character limit validation
      const charLimit = CHARACTER_LIMITS;
      if (
        value?.length > charLimit[name] &&
        name !== "marketVisitDate" &&
        name !== "visitDealerRadio" &&
        name !== "productWssRadio" &&
        name !== "darpanBillRadio"
      ) {
        setCharacterLimitValidation({
          ...characterLimitValidation,
          [name]: `Character limit exceeded (${charLimit[name]} characters max)`,
        });
      } else {
        setCharacterLimitValidation({
          ...characterLimitValidation,
          [name]: "",
        });
      }

      if (name === "marketVisitDate") {
        setDisable(false);
        checkFormData(value);
      }

      if (value === "VLC" && name === "salesGroupUserType") {
        fetchDataForVLC(rbdmCode);
      } else if (value !== "VLC" && name === "salesGroupUserType") {
        setVlcList([]);
      }
    },
    [formData, rows, debouncedApiCall, characterLimitValidation, rbdmCode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  // Escalation row handlers
  const handleInputChange = (index, field, value) => {
    const updatedRows = [...rows];
    updatedRows[index][field] = value;
    setRows(updatedRows);
    
    const newPayload = updatedRows.map(
      ({ srId, assigned_to, issue, files }) => ({
        srId,
        assigned_to,
        issue,
        files,
      })
    );
    debouncedApiCall({ ...formData, rows: newPayload });

    if (field === "assigned_to" && formValidation[`rows.${index}.assigned_to`]) {
      const updatedErrors = { ...formValidation };
      delete updatedErrors[`rows.${index}.assigned_to`];
      setFormValidation(updatedErrors);
    }
  };

  const addRow = useCallback(
    (e) => {
      e.preventDefault();
      if (rows.length < MAX_ESCALATION_LIMIT) {
        const srId = `${rows.length + 101}`;
        setRows([
          ...rows,
          {
            srId,
            assigned_to: "",
            issue: "",
            files: [],
            validationError: { file: "", issue: "" },
          },
        ]);
      }
    },
    [rows]
  );

  const removeRow = useCallback(
    (index) => {
      const updatedRows = rows.filter((_, rowIndex) => rowIndex !== index);
      setRows(updatedRows);
      
      const newPayload = updatedRows.map(
        ({ srId, assigned_to, issue, files }) => ({
          srId,
          assigned_to,
          issue,
          files,
        })
      );
      debouncedApiCall({ ...formData, rows: newPayload });
    },
    [rows, formData, debouncedApiCall]
  );

  // File upload handler
  const convertBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const fileReader = new FileReader();
      fileReader.readAsDataURL(file);
      fileReader.onload = () => {
        resolve(fileReader.result);
      };
      fileReader.onerror = (error) => {
        reject(error);
      };
    });
  };

  const handleFileChange = useCallback(
    async (index, event) => {
      event.preventDefault();
      const files = event.target.files;
      const filesArray = Array.from(files);
      const updatedRows = [...rows];

      if (!updatedRows[index].validationError) {
        updatedRows[index].validationError = { file: "", issue: "" };
      }

      if (filesArray.length > 7) {
        updatedRows[index].validationError.file =
          "Please select a maximum of 7 files";
        setRows(updatedRows);
        return;
      }

      const maxSize = 2 * 1024 * 1024;
      for (let file of filesArray) {
        if (file.size > maxSize) {
          updatedRows[index].validationError.file = `File "${file.name}" exceeds the 2MB size limit.`;
          setRows(updatedRows);
          return;
        }
      }

      updatedRows[index].validationError.file = "";

      const fileDataPromises = filesArray.map(async (file) => {
        const base64 = await convertBase64(file);
        return {
          base64: base64.replace("data:", "").replace(/^.+,/, ""),
          fileExtension: file.name.split(".").pop(),
          fileName: file.name,
        };
      });

      const fileData = await Promise.all(fileDataPromises);
      updatedRows[index].files = fileData;
      setRows(updatedRows);
    },
    [rows]
  );

  // Fetch VLC data
  const fetchDataForVLC = async (rbdmCode) => {
    try {
      setLoading(true);
      const response = await marketVisitReportService.getVLCUsers(rbdmCode);
      setLoading(false);
      setVlcList(response?.data);
    } catch (error) {
      console.error("Error fetching VLC users:", error);
      setLoading(false);
    }
  };

  // Get form dates
  const getMvrFormDate = async () => {
    setLoading(true);
    try {
      const result = await marketVisitReportService.getFormDates(userId);
      const filterResult = separateDates(result);
      setDraftHighlightedDates(filterResult.draftDates);
      setSubmittedHighlightedDates(filterResult.otherDates);
      setLoading(false);
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    getMvrFormDate();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-populate today's date once userId is available and draft init has run.
  // We track whether checkFormData has been called for today to avoid repeat calls.
  const initialLoadDoneRef = useRef(false);
  useEffect(() => {
    if (userId && !initialLoadDoneRef.current) {
      // Wait until after the draftedForm init effect has had a chance to run
      // (both effects fire on userId change; draftedFormRef is updated synchronously)
      initialLoadDoneRef.current = true;
      const today = new Date();
      const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}-${String(today.getDate()).padStart(2, "0")}`;
      // Auto-populate cluster from today's MTP tour plan (matches production setDefaultValues)
      let autoClusterCode = null;
      if (tourPlanDefaultData?.length > 0) {
        const todayRecord = tourPlanDefaultData.find(
          (rec) => rec?.Date__c === formattedDate
        );
        if (todayRecord?.Cluster_Code__c) {
          autoClusterCode = todayRecord.Cluster_Code__c;
        }
      }
      setFormData((prev) => ({
        ...prev,
        marketVisitDate: formattedDate,
        ...(autoClusterCode ? { clusterCode: autoClusterCode } : {}),
      }));
      if (autoClusterCode) {
        const match = clusterCodeOption?.find(
          (o) => o.CLUSTER_CODE__c === autoClusterCode || o.SH3_Code__c === autoClusterCode
        );
        if (match) setCluster(match);
      }
      checkFormData(formattedDate);
    }
  }, [userId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Restore Autocomplete-controlled dropdown states from stored string values
  const restoreDropdownStates = useCallback((clusterCode, salesGroupUserType, userNameVisited) => {
    // Restore cluster Autocomplete value from clusterCode string
    if (clusterCode && clusterCodeOption?.length > 0) {
      const match =
        userType === "MH3" || userType === "RBDM Collaborator"
          ? clusterCodeOption.find((o) => o.CLUSTER_CODE__c === clusterCode)
          : clusterCodeOption.find(
              (o) => o.SH3_Code__c === clusterCode || o.SH3_Name__c === clusterCode
            );
      setCluster(match || clusterCodePlaceHolder);
    } else {
      setCluster(clusterCodePlaceHolder);
    }

    // Restore selectUserType Autocomplete value from comma-separated string
    if (salesGroupUserType) {
      const myArray = salesGroupUserType.split(",");
      setSelectUserType(myArray.map((item) => ({ user_name: item.trim() })));
    } else {
      setSelectUserType([]);
    }

    // Restore vlcListType from User_Name_Visited__c comma-separated string
    if (userNameVisited) {
      const names = userNameVisited.split(",").map((n) => ({ name: n.trim() }));
      setVlcListType(names);
    } else {
      setVlcListType([]);
    }
  }, [clusterCodeOption, userType]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper: restore escalation rows from server data
  const mapFetchedSRRows = (data) => {
    const mappedRows = Array.from({ length: 5 }, (_, i) => ({
      srId: `SRID-${i + 1}`,
      assigned_to: data[`Local_Issues_${i + 1}__c`] || "",
      issue: data[`Local_Issues_value${i + 1}__c`] || "",
      files: [],
      validationError: { file: "", issue: "" },
    })).filter((row) => row.assigned_to || row.issue);
    setRows(
      mappedRows.length > 0
        ? mappedRows
        : [{ srId: "101", assigned_to: "", issue: "", files: [], validationError: { file: "", issue: "" } }]
    );
  };

  // Helper: restore escalation rows from auto-save data
  const mapAutoSaveRows = (rowData) => {
    const mappedRows = rowData
      ?.map((row, index) => ({
        srId: `SRID-${index + 1}`,
        assigned_to: row.assigned_to || "",
        issue: row.issue || "",
        files: [],
        validationError: { file: "", issue: "" },
      }))
      .filter((row) => row.assigned_to || row.issue);
    setRows(
      mappedRows?.length > 0
        ? mappedRows
        : [{ srId: "101", assigned_to: "", issue: "", files: [], validationError: { file: "", issue: "" } }]
    );
  };

  // Check form data — mirrors production logic:
  // 1. If in-memory draft exists for date → load from draftedForm (unsaved keystrokes)
  // 2. Else → load from server (draft or submitted)
  const checkFormData = async (selectedDate, action = "") => {
    if (!selectedDate) return;
    setLoading(true);

    try {
      // Use ref so we always read the latest draftedForm regardless of when this is called
      const draftIndex = draftedFormRef.current.findIndex(
        (obj) => obj.marketVisitDate === selectedDate
      );

      const result = await marketVisitReportService.getReport(dispatch, userId, selectedDate);

      if (draftIndex !== -1 && action !== "Submit") {
        // In-memory auto-save takes priority
        const draft = draftedFormRef.current[draftIndex];
        if (result?.success && result?.data?.length > 0) {
          setFormDraftId(result.data[0].Id);
        } else {
          setFormDraftId("");
        }
        setFormData(draft);
        mapAutoSaveRows(draft.rows);
        restoreDropdownStates(draft.clusterCode, draft.salesGroupUserType, draft.userVisited);
        setDisable(false);
      } else {
        // Load from server
        if (result?.success && result?.data?.length > 0) {
          const data = result.data[0];
          const isDraft = data.IsSavedDraft__c === "True";
          const isSubmitted = data.IsSavedDraft__c === "false" || data.IsSavedDraft__c === null;

          setFormDraftId(data.Id);
          setDisable(isSubmitted);

          if (isDraft && action !== "Submit") {
            setFormData({
              marketVisitDate: data.SaveFormDate__c,
              clusterCode: data.Cluster__c,
              visitedWssRadio: data.Have_you_visited_a_WSS__c,
              wssVisited: data.WSS_Name_Visited__c,
              keyObservationWss: data.Key_Observations_Issues_faced_by_WSS__c,
              productWssRadio: data.Are_competition_products_WSS__c,
              darpanBillRadio: data.Darpan_bills_dealers_visited__c,
              darpanDealer: data.Names_Dealers_Darpan_B_unavail__c,
              dealerVisited: data.Dealer_Name_Visited__c,
              issueFacedDealer: data.Key_Issues_faced_by_Dealer__c,
              deliveryFeedback: data.Service_Or_Delivery_feedback__c,
              visitDealerRadio: data.schemes_at_dealer_by_WSS__c,
              schemeComments: data.Scheme_settlement_comments__c,
              userVisited: data.User_Name_Visited__c,
              keyObsUser: data.Key_Issues_faced_by_User__c,
              issuesFacedByBank: data.Issue_Faced_While_Banking__c,
              productQuality: data.Product_Quality_Issues__c,
              newProductOpportunity: data.Product_Related_Opportunities__c,
              productOtherFeedback: data.Any_Other_Feedback_Product__c,
              competitionActivity: data.Observation_competition_activity_M_Visit__c,
              competitionProducts: data.Competition_product_affecting_sales__c,
              urgentEscalation: data.Urgent_Escalation_Points__c,
              feedbackOnUserLoyalty: data.FeedbackOnUserLoyalty__c,
              feedbackOnUserApp: data.FeedbackOnUserApp__c,
              feedbackAtUserMeeting: data.FeedbackAtUserMeeting__c,
              obsFromRedemptionCenter: data.ObsFromRedemptionCenter__c,
              salesGroupUserType: data.User_Type__c,
              largeImproveAreas: data.Large_Improvement_Areas__c,
              miscellaneousSection: data.Miscellaneous_section__c,
            });
            setIsButtonDisable(false);
            mapFetchedSRRows(data);
            restoreDropdownStates(data.Cluster__c, data.User_Type__c, data.User_Name_Visited__c);
          } else if (isSubmitted) {
            setFormData({
              marketVisitDate: data.SaveFormDate__c,
              clusterCode: data.Cluster__c,
              visitedWssRadio: data.Have_you_visited_a_WSS__c,
              wssVisited: data.WSS_Name_Visited__c,
              keyObservationWss: data.Key_Observations_Issues_faced_by_WSS__c,
              productWssRadio: data.Are_competition_products_WSS__c,
              darpanBillRadio: data.Darpan_bills_dealers_visited__c,
              darpanDealer: data.Names_Dealers_Darpan_B_unavail__c,
              dealerVisited: data.Dealer_Name_Visited__c,
              issueFacedDealer: data.Key_Issues_faced_by_Dealer__c,
              deliveryFeedback: data.Service_Or_Delivery_feedback__c,
              visitDealerRadio: data.schemes_at_dealer_by_WSS__c,
              schemeComments: data.Scheme_settlement_comments__c,
              userVisited: data.User_Name_Visited__c,
              keyObsUser: data.Key_Issues_faced_by_User__c,
              issuesFacedByBank: data.Issue_Faced_While_Banking__c,
              productQuality: data.Product_Quality_Issues__c,
              newProductOpportunity: data.Product_Related_Opportunities__c,
              productOtherFeedback: data.Any_Other_Feedback_Product__c,
              competitionActivity: data.Observation_competition_activity_M_Visit__c,
              competitionProducts: data.Competition_product_affecting_sales__c,
              urgentEscalation: data.Urgent_Escalation_Points__c,
              feedbackOnUserLoyalty: data.FeedbackOnUserLoyalty__c,
              feedbackOnUserApp: data.FeedbackOnUserApp__c,
              feedbackAtUserMeeting: data.FeedbackAtUserMeeting__c,
              obsFromRedemptionCenter: data.ObsFromRedemptionCenter__c,
              salesGroupUserType: data.User_Type__c,
              largeImproveAreas: data.Large_Improvement_Areas__c,
              miscellaneousSection: data.Miscellaneous_section__c,
            });
            setIsButtonDisable(true);
            mapFetchedSRRows(data);
            restoreDropdownStates(data.Cluster__c, data.User_Type__c, data.User_Name_Visited__c);
          }
        } else {
          // No server data — fresh form for this date
          setFormData({
            marketVisitDate: selectedDate,
            visitedWssRadio: "", visitDealerRadio: "", productWssRadio: "",
            darpanBillRadio: "", wssVisited: "", keyObservationWss: "",
            darpanDealer: "", dealerVisited: "", issueFacedDealer: "",
            deliveryFeedback: "", schemeComments: "", userVisited: "",
            keyObsUser: "", issuesFacedByBank: "", productQuality: "",
            newProductOpportunity: "", productOtherFeedback: "",
            competitionActivity: "", competitionProducts: "",
            urgentEscalation: "", createdDate: "", feedbackOnUserLoyalty: "",
            feedbackOnUserApp: "", feedbackAtUserMeeting: "",
            obsFromRedemptionCenter: "", salesGroupUserType: "",
            largeImproveAreas: "", miscellaneousSection: "", clusterCode: "",
          });
          setRows([{ srId: "101", assigned_to: "", issue: "", files: [], validationError: { file: "", issue: "" } }]);
          setFormDraftId(null);
          setDisable(false);
          setIsButtonDisable(false);
          restoreDropdownStates(null, null, null);
        }
      }
    } catch (error) {
      console.error("Error checking form data:", error);
    }

    setLoading(false);
  };

  // Save as draft handler
  const HandleSaveAsDraft = useCallback(() => {
    setIsDraftSave(true);
  }, []);

  // Validation logic
  const checkValidation = useCallback((data) => {
    const errors = {};
    const todayDate = new Date();

    if (!data?.marketVisitDate) {
      errors.marketVisitDate = "Please Select the date";
    } else if (
      data?.marketVisitDate > todayDate?.toISOString()?.split("T")?.[0]
    ) {
      errors.marketVisitDate = "Please don't select the future date";
    } else if (dayjs(data.marketVisitDate).isBefore(getMinWorkingDate(), "day")) {
      errors.marketVisitDate = "Please select a date within the last 7 working days";
    }

    // User Meeting Section for RBDM/MH3
    if (userType === "RBDM Collaborator" || userType === "MH3") {
      if (!data?.feedbackOnUserLoyalty) {
        errors.feedbackOnUserLoyalty = "Please enter the field";
      }
      if (!data?.feedbackAtUserMeeting) {
        errors.feedbackAtUserMeeting = "Please enter the field";
      }
    }

    // Dealer Section (not for MH3/RBDM)
    if (userType !== "MH3" && userType !== "RBDM Collaborator") {
      if (!data?.dealerVisited && !dealerName?.length) {
        errors.dealerVisited = "Please Enter the Key Dealer";
      }
      if (!data?.issueFacedDealer) {
        errors.issueFacedDealer = "Please Enter the observation";
      }
      if (!data?.deliveryFeedback) {
        errors.deliveryFeedback = "Please Enter the feedback";
      }
      if (!data?.schemeComments) {
        errors.schemeComments = "Please enter the feedback";
      }
    }

    // Competition Section (not for MH3/RBDM)
    if (userType !== "MH3" && userType !== "RBDM Collaborator") {
      if (!data?.competitionActivity) {
        errors.competitionActivity = "Please enter the field";
      }
      if (!data?.competitionProducts) {
        errors.competitionProducts = "Please enter the field";
      }
    }

    // Validate cluster code for escalations
    if (
      [
        "SH6",
        "SH5",
        "SH7",
        "HO Collaborator",
        "TMM",
        "HO Auditor",
        "RBDM Collaborator",
        "MH3",
      ].includes(userType)
    ) {
      const isAssignedToValid = rows.some(
        (row) => row.assigned_to && row.assigned_to.trim() !== ""
      );
      if (isAssignedToValid && !data?.clusterCode) {
        errors.clusterCode = "Please select the cluster code";
      }
    }

    // Validate escalation rows
    rows.forEach((row, index) => {
      if (!row.assigned_to && row.issue?.trim().length > 0) {
        errors[`rows.${index}.assigned_to`] =
          "Please select a value from the dropdown before entering text";
      }
    });

    return errors;
  }, [userType, dealerName, rows]);

  // Prepare payload for service request
  const preparePayloadForServiceRequest = (response) => {
    const data = {
      recordType: "Dealer",
      subject: formData?.productQuality,
      description: formData?.productQuality,
      chapterName: "Dealer Feedback",
      category: "",
      subCategory: "",
      dealerId: "",
      origin: "Mconnect Local Issues",
      MVRId: response?.data?.results[0]?.id,
      BMName: userDetailName,
      BMEmail: userEmail,
      salesgroupcode: `${salesGroupCode}`,
      SR_list: rows,
      ClusterCode: formData?.clusterCode || shCode,
    };
    return data;
  };

  // Submit handler
  const validateInput = useCallback(
    async (e) => {
      e.preventDefault();
      setIsSubmit(true);
      setFormValidation(checkValidation(formData));
    },
    [formData, checkValidation]
  );

  // Form submission effect
  useEffect(() => {
    const hasErrors = Object.values(characterLimitValidation).some(
      (error) => error !== ""
    );
    setIsSubmitedSuccessfully(false);
    
    if (
      Object?.keys(formValidation)?.length === 0 &&
      !hasErrors &&
      isSubmit &&
      isEmpty(fileValidation)
    ) {
      (async () => {
        const records = [];
        
        // Build records for each insight or single record
        const buildRecord = (insightId = null, refId = "ref1") => ({
          attributes: {
            type: "Market_Visit_Report__c",
            referenceId: refId,
          },
          SaveFormDate__c: formData?.marketVisitDate,
          Cluster__c: formData?.clusterCode,
          Have_you_visited_a_WSS__c: formData?.visitedWssRadio,
          WSS_Name_Visited__c: wssName?.length ? wssName?.toString() : formData?.wssVisited,
          Key_Observations_Issues_faced_by_WSS__c: formData?.keyObservationWss,
          Are_competition_products_WSS__c: formData?.productWssRadio,
          Darpan_bills_dealers_visited__c: formData?.darpanBillRadio,
          Names_Dealers_Darpan_B_unavail__c: formData?.darpanDealer,
          Dealer_Name_Visited__c: dealerName?.length ? dealerName?.toString() : formData?.dealerVisited,
          Key_Issues_faced_by_Dealer__c: formData?.issueFacedDealer,
          Service_Or_Delivery_feedback__c: formData?.deliveryFeedback,
          schemes_at_dealer_by_WSS__c: formData?.visitDealerRadio,
          Scheme_settlement_comments__c: formData?.schemeComments,
          User_Name_Visited__c: userName?.length ? userName?.toString() : formData?.userVisited,
          Key_Issues_faced_by_User__c: formData?.keyObsUser,
          Issue_Faced_While_Banking__c: formData?.issuesFacedByBank,
          Product_Quality_Issues__c: formData?.productQuality,
          Product_Related_Opportunities__c: formData?.newProductOpportunity,
          Any_Other_Feedback_Product__c: formData?.productOtherFeedback,
          Observation_competition_activity_M_Visit__c: formData?.competitionActivity,
          Competition_product_affecting_sales__c: formData?.competitionProducts,
          Urgent_Escalation_Points__c: formData?.urgentEscalation,
          FeedbackOnUserLoyalty__c: formData?.feedbackOnUserLoyalty,
          FeedbackOnUserApp__c: formData?.feedbackOnUserApp,
          FeedbackAtUserMeeting__c: formData?.feedbackAtUserMeeting,
          ObsFromRedemptionCenter__c: formData?.obsFromRedemptionCenter,
          User_Type__c: formData?.salesGroupUserType,
          IsSavedDraft__c: false,
          ...(insightId ? { MVR_Insight_Id__c: insightId } : {}),
          Local_Issues_1__c: rows[0]?.assigned_to ?? null,
          Local_Issues_2__c: rows[1]?.assigned_to ?? null,
          Local_Issues_3__c: rows[2]?.assigned_to ?? null,
          Local_Issues_4__c: rows[3]?.assigned_to ?? null,
          Local_Issues_5__c: rows[4]?.assigned_to ?? null,
          Local_Issues_value1__c: rows[0]?.issue ?? null,
          Local_Issues_value2__c: rows[1]?.issue ?? null,
          Local_Issues_value3__c: rows[2]?.issue ?? null,
          Local_Issues_value4__c: rows[3]?.issue ?? null,
          Local_Issues_value5__c: rows[4]?.issue ?? null,
          Large_Improvement_Areas__c: formData?.largeImproveAreas,
          Miscellaneous_section__c: formData?.miscellaneousSection,
        });

        if (selectedInsights?.length > 0) {
          selectedInsights?.forEach((insightId, index) => {
            records.push(buildRecord(insightId, `ref${index + 1}`));
          });
        } else {
          records.push(buildRecord());
        }

        const finalData = { records };
        setLoading(true);
        
        try {
          const response = await dispatch(addMarketVisitReportApi(finalData));
          
          if (response?.success === true) {
            const updatedDraft = draftedForm?.filter(
              (item) => item?.marketVisitDate !== formData?.marketVisitDate
            );
            setDraftedForm(updatedDraft);

            // Delete draft if exists
            if (formDraftId) {
              await dispatch(deleteSaveAsDraftForMVRApi(formDraftId));
            }

            // Create service request if payload exists
            const payload = preparePayloadForServiceRequest(response);
            if (payload) {
              await dispatch(addEscalationServiceRequestApi(payload));
            }

            setIsSubmitedSuccessfully(true);
            setIsSubmitModalOpen(true);
            
            // Reset form
            setFormData({
              marketVisitDate: "",
              visitedWssRadio: "",
              visitDealerRadio: "",
              productWssRadio: "",
              darpanBillRadio: "",
              wssVisited: "",
              keyObservationWss: "",
              darpanDealer: "",
              dealerVisited: "",
              issueFacedDealer: "",
              deliveryFeedback: "",
              schemeComments: "",
              userVisited: "",
              keyObsUser: "",
              issuesFacedByBank: "",
              productQuality: "",
              newProductOpportunity: "",
              productOtherFeedback: "",
              competitionActivity: "",
              competitionProducts: "",
              urgentEscalation: "",
              createdDate: "",
              feedbackOnUserLoyalty: "",
              feedbackOnUserApp: "",
              feedbackAtUserMeeting: "",
              obsFromRedemptionCenter: "",
              salesGroupUserType: "",
              largeImproveAreas: "",
              miscellaneousSection: "",
            });
            setIsSubmit(false);
            setDealerName([]);
            setUserName([]);
            setWssName([]);
            setCluster(clusterCodePlaceHolder);
            setSelectUserType([]);
            setVlcListType([]);
            setRows([{
              srId: "101",
              assigned_to: "",
              issue: "",
              files: [],
              validationError: { file: "", issue: "" },
            }]);
            checkFormData(formData?.marketVisitDate, "Submit");
            setIsButtonDisable(true);
            getMvrFormDate();
          } else {
            setErrorMessage("Your market visit report could not be submitted. Please try again.");
            setIsErrorModalOpen(true);
          }
        } catch (error) {
          console.error("Submission error:", error);
          setErrorMessage("Your market visit report could not be submitted. Please try again.");
          setIsErrorModalOpen(true);
        }
        
        setLoading(false);
      })();
    }
    
    if (Object?.keys(formValidation)?.length !== 0 || hasErrors) {
      setIsSubmit(false);
    }
  }, [isSubmit, formValidation, characterLimitValidation, fileValidation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Draft save effect
  useEffect(() => {
    const hasErrors = Object.values(characterLimitValidation).some(
      (error) => error !== ""
    );
    
    if (!hasErrors && isDraftSave) {
      (async () => {
        const draftPayload = {
          id: formDraftId ? formDraftId : "",
          SaveFormDate: formData?.marketVisitDate,
          HaveYouVisitedAWSS: formData?.visitedWssRadio,
          WSSNameVisited: wssName?.length ? wssName?.toString() : formData?.wssVisited,
          KeyObservationsIssuesFacedByWSS: formData?.keyObservationWss,
          AreCompetitionProductsWSS: formData?.productWssRadio,
          DarpanBillsDealersVisited: formData?.darpanBillRadio,
          NamesDealersDarpanBUnavail: formData?.darpanDealer,
          DealerNameVisited: dealerName?.length ? dealerName?.toString() : formData?.dealerVisited,
          KeyIssuesFacedByDealer: formData?.issueFacedDealer,
          ServiceOrDeliveryFeedback: formData?.deliveryFeedback,
          SchemesAtDealerByWSS: formData?.visitDealerRadio,
          SchemeSettlementComments: formData?.schemeComments,
          UserNameVisited: userName?.length ? userName?.toString() : formData?.userVisited,
          KeyIssuesFacedByUser: formData?.keyObsUser,
          IssueFacedWhileBanking: formData?.issuesFacedByBank,
          ProductQualityIssues: formData?.productQuality,
          ProductRelatedOpportunities: formData?.newProductOpportunity,
          AnyOtherFeedbackProduct: formData?.productOtherFeedback,
          ObservationCompetitionActivityMVisit: formData?.competitionActivity,
          CompetitionProductAffectingSales: formData?.competitionProducts,
          UrgentEscalationPoints: formData?.urgentEscalation,
          FeedbackOnUserLoyalty: formData?.feedbackOnUserLoyalty,
          FeedbackOnUserApp: formData?.feedbackOnUserApp,
          FeedbackAtUserMeeting: formData?.feedbackAtUserMeeting,
          ObsFromRedemptionCenter: formData?.obsFromRedemptionCenter,
          UserType: formData?.salesGroupUserType,
          LocalIssues1: rows[0]?.assigned_to ?? null,
          LocalIssues2: rows[1]?.assigned_to ?? null,
          LocalIssues3: rows[2]?.assigned_to ?? null,
          LocalIssues4: rows[3]?.assigned_to ?? null,
          LocalIssues5: rows[4]?.assigned_to ?? null,
          LocalIssuesValue1: rows[0]?.issue ?? null,
          LocalIssuesValue2: rows[1]?.issue ?? null,
          LocalIssuesValue3: rows[2]?.issue ?? null,
          LocalIssuesValue4: rows[3]?.issue ?? null,
          LocalIssuesValue5: rows[4]?.issue ?? null,
          LargeImprovementAreas: formData?.largeImproveAreas,
          MiscellaneousSection: formData?.miscellaneousSection,
          ClusterCode: formData?.clusterCode,
        };
        
        if (formData && isDraftSave) {
          setLoading(true);
          try {
            const response = await dispatch(updateMarketVisitReportApi(draftPayload));
            if (response?.success === true) {
              setFormDraftId(response?.data?.id);
              debouncedApiCall({
                ...formData,
                rows,
                draftId: response?.data?.id,
              });
              setIsDraftSave(false);
              setIsDraftModalOpen(true);
              checkFormData(formData?.marketVisitDate);
              setIsSubmit(false);
              getMvrFormDate();
            } else {
              setIsDraftSave(false);
              setErrorMessage("Your market visit report could not be saved as draft. Please try again.");
              setIsErrorModalOpen(true);
              setIsSubmit(false);
            }
          } catch (error) {
            console.error("Draft save error:", error);
            setErrorMessage("Your market visit report could not be saved as draft. Please try again.");
            setIsErrorModalOpen(true);
            setIsDraftSave(false);
          }
          setLoading(false);
        }
      })();
    } else {
      setIsDraftSave(false);
    }
  }, [isDraftSave, characterLimitValidation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Remove from draft if submitted successfully
  useEffect(() => {
    if (drafts[userId] && isSubmitedSuccessfully) {
      const updatedDrafts = { ...drafts, [userId]: draftedForm };
      dispatch(removeMVRDraftDetails(updatedDrafts));
    }
  }, [isSubmitedSuccessfully]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      <div className={styles.marketVisitReport}>
        <Loader loading={loading} />
        
        <form>
          <div className={styles.MarketVisitReport__mainBox}>
            {/* Date Selection & Cluster Code */}
            <DateSection
              formData={formData}
              onChange={onChange}
              setCurrentSelectedDate={setCurrentSelectedDate}
              submittedHighlightedDates={submittedHighlightedDates}
              draftHighlightedDates={draftHighlightedDates}
              formValidation={formValidation}
              cluster={cluster}
              setCluster={setCluster}
              clusterCodeOption={clusterCodeOption}
              userType={userType}
              disable={disable}
            />

            {/* User Section (MH3/RBDM only) */}
            <UserSection
              formData={formData}
              onChange={onChange}
              userName={userName}
              salesGroupUser={salesGroupUser}
              selectUserType={selectUserType}
              setSelectUserType={setSelectUserType}
              vlcList={vlcList}
              vlcListType={vlcListType}
              setVlcListType={setVlcListType}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              userType={userType}
              disable={disable}
            />

            {/* WSS Section */}
            <WSSSection
              formData={formData}
              onChange={onChange}
              wssName={wssName}
              productList={productList}
              wssList={wssList}
              dealerList={dealerList}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              userType={userType}
              disable={disable}
            />

            {/* Dealer Section */}
            <DealerSection
              formData={formData}
              onChange={onChange}
              dealerName={dealerName}
              productList={productList}
              dealerList={dealerList}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              userType={userType}
              disable={disable}
            />

            {/* Users Section (Regular users) */}
            <UsersSectionRegular
              formData={formData}
              onChange={onChange}
              userName={userName}
              productList={productList}
              wssList={wssList}
              dealerList={dealerList}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              userType={userType}
              disable={disable}
            />

            {/* Product Section */}
            <ProductSection
              formData={formData}
              onChange={onChange}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              disable={disable}
            />

            {/* Competition Section */}
            <CompetitionSection
              formData={formData}
              onChange={onChange}
              productList={productList}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              userType={userType}
              disable={disable}
            />

            {/* Escalation Section */}
            <EscalationSection
              rows={rows}
              handleInputChange={handleInputChange}
              addRow={addRow}
              removeRow={removeRow}
              handleFileChange={handleFileChange}
              productList={productList}
              wssList={wssList}
              dealerList={dealerList}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              disable={disable}
              formData={formData}
            />

            {/* Miscellaneous Section */}
            <MiscellaneousSection
              formData={formData}
              onChange={onChange}
              characterLimitValidation={characterLimitValidation}
              formValidation={formValidation}
              disable={disable}
            />
          </div>

          {/* Action Buttons */}
          <div className="d-flex">
            {!isButtonDisable && (
              <button
                type="button"
                className={`btn mt-3 ${styles.MarketVisitReport__EditButton}`}
                onClick={HandleSaveAsDraft}
                disabled={isButtonDisable}
              >
                Save As draft
              </button>
            )}
            <button
              type="button"
              className={`btn mt-3 ${styles.MarketVisitReport__SubmitButton}`}
              disabled={isButtonDisable}
              onClick={(e) => validateInput(e)}
            >
              Submit <FiArrowRight style={{ marginLeft: "2px" }} />
            </button>
          </div>

          {/* Modals */}
          <SubmitModal
            isOpen={isSubmitModalOpen}
            setIsOpen={setIsSubmitModalOpen}
          />
          <DraftModal isOpen={isDraftModalOpen} setIsOpen={setIsDraftModalOpen} />
          <ErrorModal isOpen={isErrorModalOpen} setIsOpen={setIsErrorModalOpen} message={errorMessage} />
        </form>
      </div>
    </>
  );
};

export default React.memo(MarketVisitReportForm);
